import React, { useState } from 'react';
import type { FormField, ThemeConfig } from '../../../types/forms';
import { getInputStyles } from '../../../lib/themeUtils';
import { formatBRPhone, normalizeBRPhoneDigits } from '../../../lib/phoneMask';
import { useScrollIntoViewOnFocus } from '../../../hooks/useScrollIntoViewOnFocus';

// 20 e não 16 (tamanho da máscara exibida): autofill/colagem chega com DDI
// ("+55 11 91234-5678" = 17+ chars) e o browser trunca ANTES do onChange,
// comendo o último dígito. A exibição continua ≤16 (re-formatada após
// normalizar para 11 dígitos).
const PHONE_MAX_INPUT_LENGTH = 20;

interface TextFieldProps {
  field: FormField;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  disabled?: boolean;
  themeConfig?: ThemeConfig;
}

export const TextField: React.FC<TextFieldProps> = ({
  field,
  value = '',
  onChange,
  error,
  disabled,
  themeConfig,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputStyles = getInputStyles(themeConfig, { error: !!error, focused: isFocused });
  const scrollOnFocus = useScrollIntoViewOnFocus<HTMLInputElement>();

  const isPhone = field.type === 'phone';

  const inputType = field.type === 'email' ? 'email' :
                    isPhone ? 'tel' :
                    field.type === 'url' ? 'url' :
                    field.type === 'number' ? 'number' : 'text';

  const displayValue = isPhone ? formatBRPhone(value) : value;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isPhone) {
      onChange?.(normalizeBRPhoneDigits(e.target.value));
    } else {
      onChange?.(e.target.value);
    }
  };

  return (
    <input
      type={inputType}
      inputMode={isPhone ? 'tel' : undefined}
      autoComplete={isPhone ? 'tel' : undefined}
      value={displayValue}
      onChange={handleChange}
      onFocus={(e) => { setIsFocused(true); scrollOnFocus(e); }}
      onBlur={() => setIsFocused(false)}
      placeholder={field.placeholder || (isPhone ? '(11) 9 1234-5678' : '')}
      required={field.required}
      disabled={disabled}
      minLength={field.validation?.minLength}
      maxLength={isPhone ? PHONE_MAX_INPUT_LENGTH : field.validation?.maxLength}
      min={field.validation?.min}
      max={field.validation?.max}
      pattern={field.validation?.pattern}
      className="w-full min-w-0 max-w-full px-4 py-3 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      style={inputStyles}
    />
  );
};
